import React, { useEffect, useState } from 'react';
import { Box, FormControl } from '@mui/material';

import {
    SimpleSingleSelect,
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    FilterBarContainer,
} from '@/src/ui';
import { getZeroOption, getValidSelectValue, type Option, Constants } from '@/src/utils';
import { type ExamHistoryFilterState } from '../models/filterDTOs';
import { apiRequest } from '@/src/services';

// Removed, but keeping in case we reinstate
// const ViewModeOptions: Option[] = [
//     { id: 1, name: 'By Assessment' },
//     { id: 2, name: 'By Student' },
// ];

interface ExamHistoryFiltersProps {
    filterState: ExamHistoryFilterState;
    setFilterState: React.Dispatch<React.SetStateAction<ExamHistoryFilterState>>;
    refreshTableData: (filters: ExamHistoryFilterState) => void;
}

const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectGradeOption = getZeroOption('Select Grade');
const SelectClassOption = getZeroOption('All');
const NoClassOption = getZeroOption('No Classes Available');

const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
    loading
        ? { options: [LoadingOption], value: 0, disabled: true }
        : { options: opts, value: val };

const ExamHistoryFilters: React.FC<ExamHistoryFiltersProps> = ({
    filterState,
    setFilterState,
    refreshTableData,
}) => {
    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [gradeOptions, setGradeOptions] = useState<Option[]>([LoadingOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([SelectClassOption]);
    const [, setAllClassData] = useState<any[]>([]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const requiredFields = ['districtId', 'schoolId', 'gradeLevelMasterId'] as const;
    function getMissingFields(fs: any): string[] {
        return requiredFields
            .filter((key) => !fs[key] || Number(fs[key]) === 0)
            .map((key) => Constants.fieldLabels[key]);
    }

    useEffect(() => {
        async function fetchInitialFiltersAndDependents() {
            setFiltersLoading(true);
            try {
                const res = await apiRequest<any>(
                    'GET',
                    'utility/filterData?getDistrict=true&getGradeLevelMaster=true'
                );
                setDistrictOptions([SelectDistrictOption, ...(res.data.districtOptions ?? [])]);
                setGradeOptions([SelectGradeOption, ...(res.data.gradeLevelMasterOptions ?? [])]);

                if (
                    filterState.districtId &&
                    filterState.districtId !== Number(SelectDistrictOption.id)
                ) {
                    await fetchSchoolClassOptions(
                        filterState.districtId,
                        filterState.schoolId,
                        filterState.classId
                    );
                }
            } catch {
                setDistrictOptions([SelectDistrictOption]);
                setGradeOptions([SelectGradeOption]);
                setSchoolOptions([SelectSchoolOption]);
                setClassOptions([SelectClassOption]);
            } finally {
                setFiltersLoading(false);
            }
        }
        fetchInitialFiltersAndDependents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setFilterState]);

    const fetchSchoolClassOptions = async (
        districtId: number | string,
        savedSchoolId?: number,
        savedClassId?: number
    ) => {
        if (!districtId || districtId === Number(SelectDistrictOption.id)) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(SelectSchoolOption.id),
                gradeLevelMasterId: Number(SelectGradeOption.id),
                classId: Number(SelectClassOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<any>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;
            const sortedSchools = (data.schoolOptions ?? []).sort((a: any, b: any) =>
                a.name.localeCompare(b.name)
            );
            setSchoolOptions([SelectSchoolOption, ...sortedSchools]);
            const validSchoolId =
                savedSchoolId && sortedSchools.some((opt: any) => opt.id === savedSchoolId)
                    ? savedSchoolId
                    : Number(SelectSchoolOption.id);
            setFilterState((prev) => ({ ...prev, schoolId: Number(validSchoolId) }));

            const sortedClassData = (data.classOptions ?? []).sort((a: any, b: any) =>
                a.name.localeCompare(b.name)
            );
            setAllClassData(sortedClassData);
            let filteredClassOptions = sortedClassData;
            if (validSchoolId && validSchoolId !== Number(SelectSchoolOption.id)) {
                filteredClassOptions = filteredClassOptions.filter(
                    (opt: any) => opt.schoolId === Number(validSchoolId)
                );
            }
            const gradeId = filterState.gradeLevelMasterId;
            if (gradeId && gradeId !== Number(SelectGradeOption.id)) {
                filteredClassOptions = filteredClassOptions.filter((opt: any) =>
                    Array.isArray(opt.gradeLevelMasterIds)
                        ? opt.gradeLevelMasterIds.includes(Number(gradeId))
                        : true
                );
            }
            filteredClassOptions = filteredClassOptions.sort((a: any, b: any) =>
                a.name.localeCompare(b.name)
            );
            const classOptionsArr = filteredClassOptions.length
                ? [
                      SelectClassOption,
                      ...filteredClassOptions.map((opt: any) => ({ id: opt.id, name: opt.name })),
                  ]
                : [NoClassOption];
            setClassOptions(classOptionsArr);

            const validClassId =
                savedClassId && filteredClassOptions.some((opt: any) => opt.id === savedClassId)
                    ? savedClassId
                    : Number(SelectClassOption.id);
            setFilterState((prev) => ({ ...prev, classId: Number(validClassId) }));
            setError(null);
        } catch {
            setError(
                'Failed to fetch school and class options. Please refresh the page or try again.'
            );
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
        }
    };

    useEffect(() => {
        if (filtersLoading) return;
        if (filterState.districtId && filterState.districtId !== Number(SelectDistrictOption.id)) {
            fetchSchoolClassOptions(
                filterState.districtId,
                filterState.schoolId,
                filterState.classId
            );
        } else {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.districtId, filterState.gradeLevelMasterId]);

    return (
        <Box
            sx={{
                mb: 2,
                gap: 2,
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
                justifyContent: 'center',
            }}
        >
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="districtId"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            districtId: newDistrictId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                        }));
                        fetchSchoolClassOptions(newDistrictId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="schoolId"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            schoolId: newSchoolId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                        }));
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.districtId === Number(SelectDistrictOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="gradeLevelMasterId"
                    label="Grade *"
                    {...getLoadingProps(
                        gradeOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeOptions,
                                gradeOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            gradeLevelMasterId: newGradeId,
                            classId: Number(SelectClassOption.id),
                        }));
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.districtId === Number(SelectDistrictOption.id) ||
                        filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="classId"
                    label="Class"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId ?? 0,
                                classOptions,
                                classOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, classId: Number(e.target.value) }));
                    }}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>
            <FilterBarContainer>
                <FormControl size="small" sx={{ minWidth: 120, maxWidth: 400, mb: 0.5, mt: '4px' }}>
                    {/* Removed, but keeping in case we reinstate */}
                    {/* <InputLabel
                        shrink
                        htmlFor="view-mode-radio"
                        sx={{ background: 'white', px: 0.5 }}
                    >
                        View Mode
                    </InputLabel>
                    <Box
                        id="view-mode-radio"
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1.5,
                            border: (theme) => `1px solid ${theme.palette.grey[400]}`,
                            borderRadius: 1,
                            backgroundColor: (theme) => theme.palette.background.paper,
                            px: 2,
                            py: 1.5,
                            maxHeight: 40,
                        }}
                    >
                        {ViewModeOptions.map((option) => (
                            <SimpleRadio
                                key={option.id}
                                name="view-mode"
                                label={option.name}
                                value={String(option.id)}
                                checked={
                                    (filterState.pendingViewMode ?? filterState.viewMode) ===
                                    Number(option.id)
                                }
                                onChange={() =>
                                    setFilterState((prev) => ({
                                        ...prev,
                                        pendingViewMode: Number(option.id),
                                    }))
                                }
                                sx={{ marginRight: 0 }}
                            />
                        ))}
                    </Box> */}
                </FormControl>
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setError(
                                `Please select the following required fields before filtering: ${missing.join(', ')}.`
                            );
                            return;
                        }
                        setError(null);
                        setFilterState((prev) => ({
                            ...prev,
                            viewMode: prev.pendingViewMode ?? prev.viewMode,
                            pendingViewMode: undefined,
                        }));
                        refreshTableData({
                            ...filterState,
                            viewMode: filterState.pendingViewMode ?? filterState.viewMode,
                        });
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ padding: '8px 25px' }}
                    disabled={filtersLoading}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>
            <ErrorMessage error={error} style={{ textAlign: 'center', marginBottom: '8px' }} />
        </Box>
    );
};

export default ExamHistoryFilters;
